// Import auth utilities
import { checkResponseStatus } from '../utils/authUtils';

// Helper function to get token - avoids circular dependency
const getAuthToken = () => {
    // Try to get from localStorage first (fallback)
    return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
};

export async function fetchProtocols(page = 1, limit = 8) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    const headers = {
        'Content-Type': 'application/json',
    };

    // Add authorization header and cookie with JWT token
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    console.log('Making fetch protocols request to:', `${API_BASE_URL}api/v1/dashboard/protocol/?page=${page}&limit=${limit}`);

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/protocol/?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    let data;
    try {
        data = await response.json();
    } catch (e) {
        // fallback for non-JSON error
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status} ${response.statusText}`);
    }

    if (!response.ok) {
        const errorMessage = data.message || `Server error: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
    }
    return data;
}

export async function createProtocol({ company_name, description, logo }) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    const accessToken = getAuthToken();
    const headers = {};
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    const formData = new FormData();
    formData.append('company_name', company_name);
    formData.append('description', description);
    if (logo) {
        formData.append('logo', logo);
    }

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/protocol/create`, {
        method: 'POST',
        headers,
        body: formData,
    });

    // Check for 401 status
    checkResponseStatus(response);

    let data;
    try {
        data = await response.json();
    } catch (e) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status} ${response.statusText}`);
    }

    if (!response.ok) {
        const errorMessage = data.message || `Server error: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
    }

    return data;
}

export async function fetchProtocolById(id) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    const accessToken = getAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/protocol/${id}`, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    let data;
    try {
        data = await response.json();
    } catch (e) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status} ${response.statusText}`);
    }

    if (!response.ok) {
        const errorMessage = data.message || `Server error: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
    }

    return data;
}

export async function deleteProtocol(id) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    const accessToken = getAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/protocol/delete/${id}`, {
        method: 'DELETE',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    let data;
    try {
        data = await response.json();
    } catch (e) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status} ${response.statusText}`);
    }

    if (!response.ok) {
        const errorMessage = data.message || `Server error: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
    }

    return data;
}

export async function updateProtocol(id, { company_name, description, logo }) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    const accessToken = getAuthToken();
    const headers = {};
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    const formData = new FormData();
    if (company_name !== undefined) formData.append('company_name', company_name);
    if (description !== undefined) formData.append('description', description);
    if (logo) formData.append('logo', logo);

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/protocol/update/${id}`, {
        method: 'PATCH',
        headers,
        body: formData,
    });

    // Check for 401 status
    checkResponseStatus(response);

    let data;
    try {
        data = await response.json();
    } catch (e) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status} ${response.statusText}`);
    }

    if (!response.ok) {
        const errorMessage = data.message || `Server error: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
    }

    return data;
}

export async function fetchProtocolCustomers(protocolId) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    const headers = {
        'Content-Type': 'application/json',
    };

    // Add authorization header and cookie with JWT token
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    console.log('Making fetch protocol customers request to:', `${API_BASE_URL}api/v1/dashboard/protocol/get-protocol-offer/${protocolId}`);

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/protocol/get-protocol-offer/${protocolId}`, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    let data;
    try {
        data = await response.json();
    } catch (e) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status} ${response.statusText}`);
    }

    if (!response.ok) {
        const errorMessage = data.message || `Server error: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
    }

    // Return the customers array from the nested data structure
    return data.data?.data || [];
}